import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Form, FormInstance, Typography, Upload, UploadProps } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  floorDetailState,
  isUploadingMapState,
  listPointState
} from 'store/buildingStore'
import useSWR from 'swr'
import { FloorMapElement } from 'types/building'
import Utils from 'utils'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import Title from 'components/atoms/Title'

type Props = {
  form: FormInstance<any>
}

const { Dragger } = Upload

export default function UploadMapModal({ form }: Props) {
  const { t } = useTranslation()
  const { id, floorId } = useParams()
  const stopPropagation = useRef<boolean>(false)
  const { navigate } = useDirectory()

  const types = ['jpg', 'jpeg', 'png']

  const [isLoading, setIsLoading] = useState(false)

  const listPoint = useRecoilValue(listPointState)
  const [isUploadingMap, setIsUploadingMap] =
    useRecoilState(isUploadingMapState)
  const [floorDetail, setFloorDetail] = useRecoilState(floorDetailState)

  const handleOpenDialog = () => {
    const mapUpload = document.getElementById('map-upload-modal')
    stopPropagation.current = true
    mapUpload && mapUpload.click()
  }

  const { trigger: handleEditFloor } = useMutation(`v1/floors/${floorId}`)

  const { trigger: handleChangePointsPosition } = useMutation(
    'v1/points/change-position'
  )

  const { mutate: getDetailFloor } = useSWR(
    {
      url: `v1/floors/${floorId}`
    },
    {
      revalidateOnMount: false
    }
  )

  const { mutate: refetchMapItems } = useSWR<FloorMapElement>(
    {
      url: 'v1/points',
      params: {
        buildingId: id,
        floorId
      }
    },
    { revalidateOnMount: false }
  )

  const handleClose = () => {
    setIsLoading(false)
    setIsUploadingMap({
      isOpen: false,
      mapFile: null
    })
  }

  const getMeta = (url: string): Promise<HTMLImageElement | undefined> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject()
      img.src = url
    })
  }

  const handleSubmit = async () => {
    if (isLoading || !id || !floorId) return
    if (!isUploadingMap.mapFile) {
      form.setFields([
        {
          name: 'mapUploadWrapper',
          errors: [t('common.validationError.fileRequired')]
        }
      ])
      return
    }
    setIsLoading(true)
    try {
      // Use original image without resize to maintain quality
      const resizedImage = isUploadingMap.mapFile
      let widthRatio = 1
      let heightRatio = 1
      if (floorDetail?.mapFile) {
        const originalMapMeta = await getMeta(floorDetail.mapFile.s3Path)
        const newMapMeta = await getMeta(
          URL.createObjectURL(resizedImage as File)
        )
        widthRatio = (newMapMeta?.width || 1) / (originalMapMeta?.width || 1)
        heightRatio = (newMapMeta?.height || 1) / (originalMapMeta?.height || 1)
        if (widthRatio !== 1 || heightRatio !== 1) {
          await handleChangePointsPosition({
            method: 'put',
            data: {
              building_id: +id,
              floor_id: +floorId,
              positions: listPoint.map((point) => ({
                id: point.id,
                x: point.x * widthRatio,
                y: point.y * heightRatio
              }))
            }
          })
        }
      }
      const formData = new FormData()
      formData.append('mapFile', resizedImage as File)
      await handleEditFloor({
        form,
        method: 'patch',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
        successMessage: t('building.saved')
      })
      const floor = await getDetailFloor()
      if (widthRatio !== 1 || heightRatio !== 1) {
        await refetchMapItems()
      }
      setFloorDetail(floor)
      handleClose()
    } catch (error) {
      Utils.handleErrorNavigate(error, navigate)
      handleClose()
    }
  }

  const readFile = (file: File) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = (e) => {
        resolve(e)
      }
      img.onerror = () => {
        reject()
      }
      img.src = window.URL.createObjectURL(file)
    })
  }

  const isTypeInclude = (name: string, types: string[]) => {
    return Boolean(
      types.find((type) => name.split('.').pop()?.toLowerCase().includes(type))
    )
  }

  const uploadProps: UploadProps = {
    name: 'map-upload-modal',
    multiple: false,
    showUploadList: false,
    beforeUpload: async (file) => {
      if (!isTypeInclude(file.name, types)) {
        form.setFields([
          {
            name: 'mapUploadWrapper',
            errors: [t('common.validationError.fileNotAllowed')]
          }
        ])
        return false
      }
      if (file.size && file.size / (1024 * 1024) > 50) {
        form.setFields([
          {
            name: 'mapUploadWrapper',
            errors: [t('common.validationError.fileTooLarge')]
          }
        ])
        return false
      }
      await readFile(file)
        .then(() => {
          setIsUploadingMap((prev) => ({
            ...prev,
            mapFile: file
          }))
          form.resetFields(['mapUploadWrapper'])
        })
        .catch(() => {
          form.setFields([
            {
              name: 'mapUploadWrapper',
              errors: [t('translation.brokenFileMessage')]
            }
          ])
        })
      return false
    },
    fileList: []
  }

  const handleEnterSubmit = () => {
    if (stopPropagation.current) {
      return
    }
    handleSubmit()
  }

  return (
    <Modal
      onCancel={handleClose}
      width={580}
      isEnterable
      onEnter={handleEnterSubmit}
    >
      <Spinner spinning={isLoading}>
        <Title title={t('building.mapUploadTitle')} bottomMargin="mb-8" />

        <Form form={form}>
          <Form.Item
            name="mapUploadWrapper"
            className="mb-0 thin-error-message"
          >
            <Dragger
              id="map-upload-modal"
              openFileDialogOnClick={false}
              {...uploadProps}
              onChange={() => {
                stopPropagation.current = false
              }}
            >
              <div className="grid gap-3 justify-items-center mx-[91px] my-2">
                <Typography className="text-[14px] leading-[21px]">
                  {isUploadingMap.mapFile
                    ? isUploadingMap.mapFile.name
                    : t('building.mapUploadGuiden')}
                </Typography>
                <Button
                  type="primary"
                  sizing="w-40 h-10"
                  onClick={handleOpenDialog}
                >
                  {t('building.selectFile')}
                </Button>
              </div>
            </Dragger>
          </Form.Item>
        </Form>

        <div className="flex gap-10 justify-center mt-10">
          <Button sizing="w-[140px] h-[50px]" onClick={handleClose}>
            {t('building.buttonCancel')}
          </Button>

          <Button
            type="primary"
            sizing="w-[140px] h-[50px]"
            onClick={handleSubmit}
          >
            {t('building.buttonSave')}
          </Button>
        </div>
      </Spinner>
    </Modal>
  )
}
